import { onBeforeUnmount, ref } from 'vue'
import { apiGet } from './apiGet'
import { apiMutate } from './apiMutate'

// Drives the "Run lint now" / "Run synthesis now" affordance on the
// admin review queues. Both run resources share the same shape:
// POST enqueues a job and returns 202 BEFORE the run row lands, and the
// runs index lists rows newest-first with a `status` of
// running | succeeded | failed. So we optimistically lock the button on
// a successful POST, then poll the index until the head run is no longer
// running (or a hard cap fires, so a stuck run can't disable the button
// forever).

// Poll cadence after a trigger and a hard cap. 4s keeps the button
// responsive without hammering the index; 75 ticks (= 5 min) bounds the
// loop so a crashed/never-finishing run still releases the lock.
const RUN_POLL_INTERVAL_MS = 4_000
const RUN_POLL_MAX_TICKS = 75

type RunResource = 'lint_runs' | 'synthesis_runs'

interface RunRow {
  id: number
  status: string
}
interface RunsPayload {
  runs: RunRow[]
}

export function useReviewQueueRun(resource: RunResource) {
  const running = ref(false)
  const triggering = ref(false)
  let ticks = 0
  let timer: ReturnType<typeof setTimeout> | null = null

  function stopPolling(): void {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    ticks = 0
  }

  // Read the runs index and reflect whether the most recent run is live.
  // `recent` orders newest-first server-side, so the head row is the one
  // that decides the lock.
  async function refresh(): Promise<void> {
    const body = await apiGet<RunsPayload>(`/api/v1/${resource}`, {
      failureLabel: `Failed to load ${resource}`,
    })
    running.value = body?.runs?.[0]?.status === 'running'
  }

  function scheduleNextPoll(): void {
    timer = setTimeout(async () => {
      ticks += 1
      try {
        await refresh()
      } catch {
        // A transient index failure shouldn't abort the loop; keep
        // polling until the run clears or the cap fires.
      }
      if (!running.value || ticks >= RUN_POLL_MAX_TICKS) {
        stopPolling()
        return
      }
      scheduleNextPoll()
    }, RUN_POLL_INTERVAL_MS)
  }

  // Enqueue a run. Throws on failure so the caller can surface an error
  // toast; the `finally` only clears the in-flight POST flag.
  async function trigger(): Promise<void> {
    triggering.value = true
    try {
      await apiMutate(`/api/v1/${resource}`, {
        method: 'POST',
        failureLabel: `${resource} run failed`,
      })
      // Lock immediately — the 202 lands before the run row exists, so a
      // refresh right now could still see the previous (finished) run.
      running.value = true
      stopPolling()
      scheduleNextPoll()
    } finally {
      triggering.value = false
    }
  }

  onBeforeUnmount(stopPolling)

  return { running, triggering, refresh, trigger }
}
